import { ProjectilePool } from './ProjectilePool.js';
import { WEAPON_FIRE_FLASH_DURATION } from './Weapon.js';
import { resolveOverlaps } from './OverlapResolver.js';
import { VIRTUAL_WIDTH, VIRTUAL_HEIGHT } from '../../../config/constants.js';

// Applied on a landed dash-attack. The player recoil is a velocity (px/sec)
// fed into Player's decaying knockback; the minion shove is a one-shot
// position nudge (px), since minions just path back on their own.
const DASH_PLAYER_BOUNCE = 260; // px/sec
const DASH_ENEMY_KNOCKBACK = 26; // px

// Soft cap on the stacked damage-multiplier chain (meta upgrades, character
// passive, pet bonus, in-run damage passives — see damageMultiplier below).
// Those sources are each individually reasonable but all compound
// multiplicatively into one uncapped number, letting a heavily-stacked run
// runaway well past what any single boss fight was tuned around. Diminishing
// returns above the 1.0 baseline (mirrors weaponDamageMultiplier's existing
// soft-cap curve below) asymptotically approach DAMAGE_MULTIPLIER_CAP as the
// raw stacked value grows, rather than clamping hard at a threshold — a
// build that's only slightly stacked barely notices the curve, while an
// extreme stack still keeps growing, just far more slowly.
const DAMAGE_MULTIPLIER_CAP = 3; // effective multiplier raw stacking asymptotically approaches
const DAMAGE_MULTIPLIER_SOFT_CAP_K = 1 / (DAMAGE_MULTIPLIER_CAP - 1);

// Orbit Ward's level 4+ damage-over-time (see OrbitWard.js). Stacks (each
// re-application refreshes duration and adds a stack, up to the cap) rather
// than refreshing a single flat tick, so a longer sustained overlap with the
// ring actually ramps up, not just re-applies the same tick forever.
const DOT_TICK_INTERVAL = 0.5;
const DOT_STACK_DURATION = 3;
const DOT_MAX_STACKS = 5;

// Solaris's Ignite (legendary pet unique, see data/pets.js): every landed
// hit banks a fraction of its final damage as a burn pool that drains in
// discrete ticks over IGNITE_DURATION. Interval ticks (not per-frame)
// for the same reason as the Orbit Ward DoT above: each burn tick goes
// through takeDamage, which drives the boss:damaged juice (particles/
// shake/hit-stop) — per-frame ticks would fire that 60 times a second.
// Re-applications add to the pool and refresh the window rather than
// tracking discrete stacks.
const IGNITE_DURATION = 3;
const IGNITE_TICK_INTERVAL = 0.5;

// Thorn's Briar Aura (see data/characters.js): a constant ring of damage
// around the player. Ticks on an interval rather than per-frame for the same
// reason the DoT and Ignite above do — each tick drives the full damage-event
// juice stack, and 60 of those a second is a wall of noise.
const AURA_TICK_INTERVAL = 0.25;

// The arena is bigger than the camera's view (Camera.js), so an enemy can
// exist without being visible. Checked against camera.x/y (the view center,
// pre-shake) rather than Camera.worldToScreen so screen shake doesn't make
// targeting flicker on/off frame to frame. Divided by camera.zoom (defaults
// to 1 for callers/tests that stub a camera without one) since the visible
// half-extent in world space shrinks as the camera zooms in and grows as it
// zooms out to keep a far-apart player/boss both in frame.
function isOnScreen(entity, camera) {
  const zoom = camera.zoom ?? 1;
  return Math.abs(entity.x - camera.x) <= VIRTUAL_WIDTH / 2 / zoom + entity.radius
    && Math.abs(entity.y - camera.y) <= VIRTUAL_HEIGHT / 2 / zoom + entity.radius;
}

/**
 * Orchestrates weapon auto-fire, projectile update, and hit detection
 * between player projectiles and every enemy on screen — the boss, plus
 * any minions from the current wave (see MinionWaveSystem). `boss` is kept
 * as its own field (not just another registry entry) since Game.js and the
 * boss-specific phase/desperation logic still need direct access to it.
 */
export class CombatSystem {
  constructor(eventBus) {
    this.eventBus = eventBus;
    this.pool = new ProjectilePool();
    this.weapons = [];
    this.boss = null;
    this.minions = [];
    // Applied to every hit at collision time — meta upgrades (M4) and
    // in-run "Power" level-up picks both just add to this one number.
    // Raw, unbounded product of every multiplicative source (meta upgrades,
    // character passive, pet bonus, in-run damage passives) — read through
    // effectiveDamageMultiplier below rather than directly, so none of those
    // sources need their own cap.
    this.damageMultiplier = 1;
    // Fraction of each landed hit banked as an Ignite burn (0 = off) —
    // set by Game.js when the Solaris pet is owned.
    this.ignitePercent = 0;
    // Set from the chosen character's `dashAttack` field (see
    // data/characters.js) — null for characters that don't have one.
    this.dashAttack = null;
    this._dashHitLanded = false;
    // Names of weapons spent as fusion parents (see fuseWeapons). They leave
    // `weapons`, so an "is it owned?" check alone would happily re-offer
    // Spirit Lance as a brand-new weapon right after it was consumed into
    // Storm Lance — ProgressionSystem._buildChoices reads this to keep a
    // fused-away parent out of the new-weapon pool for the rest of the run.
    this.consumedByFusion = new Set();

    // Answerable bullets (BOSS-REWORK.md §B9). Hostile bullet pools — the
    // boss's SpawningSystem(s), MinionWaveSystem — registered by Game so a
    // player shot can hit a `shootable` bullet the same way it hits an enemy.
    // Ordinary bullets are untouched: the pass filters on one boolean, and a
    // roster with no shootable cards pays a walk over bullets it ignores.
    // Anything with `forEachBullet(fn)` qualifies, the same contract
    // bulletCancel takes.
    this.hostilePools = [];

    // --- champion signature halves that resolve at hit time ---
    // Both null for champions without them, which makes every consumer below
    // a cheap no-op rather than something that needs gating at the call site.
    this.aura = null; // Thorn's Briar Aura: { radius, damagePerSecond, closeRangeBonus }
    this._auraTimer = AURA_TICK_INTERVAL;
    // Searing Corona (Rainbow Corona pet, data/pets.js): a pet-owned second
    // aura slot, ticked independently so it works with or without a champion
    // aura. { radius, damagePerSecond }; null when the pet isn't equipped.
    this.petAura = null;
    this._petAuraTimer = AURA_TICK_INTERVAL;
    this.dashChain = null; // Kestrel's Slipstream: { perStack, maxStacks, window, refundCooldown }
    this._dashChainStacks = 0;
    this._dashChainTimer = 0;
    this._dashRefundTimer = 0;

    // --- ultimate-driven flags (data/ultimates.js) ---
    // Neutral outside an active ultimate; the owning def's onEnd restores
    // each one (UltimateSystem guarantees onEnd runs).
    this.dashDamageMultiplier = 1; // Skybreaker
    this.dashRadiusMultiplier = 1; // Skybreaker
    this.guaranteedCrit = false; // Zero Hour
  }

  /** Kestrel's Slipstream chain: current damage bonus on the dash strike. */
  get dashChainMultiplier() {
    if (!this.dashChain) return 1;
    return 1 + this._dashChainStacks * this.dashChain.perStack;
  }

  get dashChainStacks() {
    return this._dashChainStacks;
  }

  setBoss(boss) {
    this.boss = boss;
  }

  /** Registers a hostile bullet pool for the §B9 shootable-bullet pass. */
  addHostilePool(pool) {
    if (pool && !this.hostilePools.includes(pool)) this.hostilePools.push(pool);
  }

  removeHostilePool(pool) {
    const i = this.hostilePools.indexOf(pool);
    if (i !== -1) this.hostilePools.splice(i, 1);
  }

  /**
   * Player shots vs. answerable bullets (§B9). A destroyed bullet emits
   * `bullet:destroyed` carrying the bullet itself, so Game can pay it out
   * through the same cancel conversion a phase break uses — the payoff
   * plumbing already exists, this only gives it a second trigger.
   *
   * A shot is CONSUMED by the bullet it breaks (piercing spends one charge,
   * as it does on a minion) — an answerable card should cost something to
   * answer, or the answer isn't a decision.
   */
  _checkShootableBullets(player) {
    if (!this.hostilePools.length) return;
    const dmgMultiplier = this._getDamageMultiplier(player);
    this.pool.forEachActive((proj) => {
      if (proj.hitCooldown > 0 || !proj.active) return;
      for (const pool of this.hostilePools) {
        if (!pool || typeof pool.forEachBullet !== 'function') continue;
        pool.forEachBullet((bullet) => {
          if (!bullet.shootable || !bullet.active || !proj.active || proj.hitCooldown > 0) return;
          const dx = proj.x - bullet.x;
          const dy = proj.y - bullet.y;
          if (Math.hypot(dx, dy) >= proj.radius + bullet.radius) return;
          const destroyed = bullet.takeDamage(proj.damage * dmgMultiplier);
          if (destroyed) this.eventBus.emit('bullet:destroyed', { bullet, x: bullet.x, y: bullet.y });
          this._emitHit(proj, bullet, dx, dy);
          proj.hitsRemaining--;
          if (proj.hitsRemaining <= 0) proj.active = false;
          else proj.hitCooldown = proj.hitCooldownDuration;
        });
      }
    });
  }

  addMinion(minion) {
    // Cosmetic-only timestamp for the spawn flourish (see Game.js's
    // _drawMinions) — the minion is in the registry and fully live
    // (moving, firing, hittable) from this very call, exactly as before.
    minion.spawnedAt = performance.now();
    this.minions.push(minion);
  }

  removeMinion(minion) {
    const i = this.minions.indexOf(minion);
    if (i !== -1) this.minions.splice(i, 1);
  }

  addWeapon(weapon) {
    this.weapons.push(weapon);
  }

  /**
   * Harmonics (data/fusions.js): removes both parent weapons and adds the
   * fused one. Parents with standing projectiles (Orbit Ward's ring) get
   * dispose()d so nothing orbits ownerless afterward. uniqueWeaponCount
   * drops by one as a side effect — that soft-cap relief is part of the
   * fusion's designed payoff, not an accident.
   */
  fuseWeapons(fusionDef) {
    const removed = this.weapons.filter((w) => fusionDef.parents.includes(w.name));
    for (const weapon of removed) {
      weapon.dispose?.();
      this.consumedByFusion.add(weapon.name);
    }
    this.weapons = this.weapons.filter((w) => !fusionDef.parents.includes(w.name));
    const fused = fusionDef.create();
    this.weapons.push(fused);
    this.eventBus.emit('weapon:fused', { name: fusionDef.name });
    return fused;
  }

  get uniqueWeaponCount() {
    return new Set(this.weapons.map((weapon) => weapon.name)).size;
  }

  // Extra weapon slots increase build coverage, so total output gets a soft
  // diminishing-return curve. The first weapon is never penalized and the
  // floor prevents a six-plus-weapon build from becoming unusably weak.
  get weaponDamageMultiplier() {
    const extraWeapons = Math.max(0, this.uniqueWeaponCount - 1);
    return Math.max(0.6, 1 / (1 + 0.1 * extraWeapons));
  }

  /** Soft-capped read of damageMultiplier — see DAMAGE_MULTIPLIER_CAP above. */
  get effectiveDamageMultiplier() {
    const extra = Math.max(0, this.damageMultiplier - 1);
    return 1 + extra / (1 + DAMAGE_MULTIPLIER_SOFT_CAP_K * extra);
  }

  _getDamageMultiplier(player) {
    return this.effectiveDamageMultiplier * this.weaponDamageMultiplier
      * player.surgeDamageMultiplier * (player.adrenalineDamageMultiplier ?? 1)
      // Level-up cards with a condition of their own (data/levelUpPool.js):
      // Bulletdancer's banked graze stacks and Pristine Edge's full-HP
      // bonus. Both are 1 until their card is picked.
      * (player.grazeDamageMultiplier ?? 1) * (player.pristineDamageMultiplier ?? 1);
  }

  /**
   * Opening Gambit (level-up card): extra damage against a target that
   * hasn't been scratched yet. Per-target rather than global, so it can't go
   * through _getDamageMultiplier with the rest — it rewards opening on fresh
   * minions in a wave, and lands exactly once on a boss.
   */
  _fullHpTargetMultiplier(player, enemy) {
    const bonus = player.fullHpTargetDamageBonus ?? 0;
    if (bonus <= 0 || !enemy.maxHp) return 1;
    return enemy.hp >= enemy.maxHp ? 1 + bonus : 1;
  }

  /**
   * The Mirror-Shade (Act II, entities/MirrorShade.js) carries a tone, and
   * only a player attuned to that same tone can hurt it — polarity as a
   * *dealing* problem, the mirror of the Antiphon's taking one. Enforced here
   * rather than in the entity because this is where the player is in scope.
   * False for every enemy without a `tone`, which is all of them but this one.
   */
  _toneMismatched(player, enemy) {
    return !!enemy.tone && !!player.attunement && player.attunement !== enemy.tone;
  }

  /** Boss + every alive minion — the full set of things weapons/collision can hit. */
  _getEnemies() {
    return this.boss ? [this.boss, ...this.minions] : this.minions;
  }

  /**
   * Closest enemy to `origin` (player or dash origin) — what weapons aim at
   * instead of always the boss. When `camera` is given, only considers
   * enemies currently visible on screen — the arena is bigger than the
   * camera's view (Camera.js), so weapons shouldn't auto-aim at something
   * the player can't even see. Omitting `camera` (e.g. the weapon-icon
   * facing helper in Game.js) keeps the old unrestricted behavior.
   */
  getNearestTarget(origin, camera) {
    let nearest = null;
    let nearestDist = Infinity;
    // Invulnerable enemies (a shielded Warden, or the boss mid-desperation)
    // are tracked separately and only returned as a last resort — otherwise
    // auto-aim weapons lock onto a target that can't take damage while
    // killable enemies stand right next to it. When literally everything on
    // screen is invulnerable (or it's the boss alone, its normal case),
    // falling back to it keeps weapons visually aiming somewhere instead of
    // going idle — CombatSystem._checkCollisions still no-ops the actual hit.
    let nearestInvuln = null;
    let nearestInvulnDist = Infinity;
    for (const enemy of this._getEnemies()) {
      if (camera && !isOnScreen(enemy, camera)) continue;
      const dist = Math.hypot(enemy.x - origin.x, enemy.y - origin.y);
      if (enemy.invulnerable) {
        if (dist < nearestInvulnDist) {
          nearestInvulnDist = dist;
          nearestInvuln = enemy;
        }
        continue;
      }
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = enemy;
      }
    }
    return nearest ?? nearestInvuln;
  }

  update(dt, player, camera) {
    // Runs first so nothing else this frame (targeting, collision, render)
    // reads a position that's still overlapping — everyone else has already
    // moved for this frame by the time CombatSystem.update runs (Game.js
    // calls Player/MinionWaveSystem/Boss update before combat.update).
    resolveOverlaps(this.minions, this.boss, player);

    const target = this.getNearestTarget(player, camera);
    // The Great Hush: while muted (standing in a hush zone) the player's
    // weapons fall dumb — no new fire. Shots already in flight (below) keep
    // going; only the fire loop is gated.
    if (target && !player.muted) {
      // Feeding weapons a scaled-up dt (rather than touching their fire-rate
      // config) is what "weapon fire-rate up" during an ultimate surge
      // (and the Rapid Fire passive, Overclock pet, and Vess's Cadence)
      // actually is — each weapon's own timer just reaches zero sooner.
      // `?? 1` covers test mocks/callers that predate these fields.
      const weaponDt = dt * player.surgeFireRateMultiplier * (player.fireRateMultiplier ?? 1)
        * (player.cadenceFireRateMultiplier ?? 1)
        // Act III champions: Quill's glyph (standing on your own mark) and
        // Fugue's Statement stance. Both 1 for everyone else.
        * (player.inscriptionFireRateMultiplier ?? 1) * (player.stanceFireRateMultiplier ?? 1);
      for (const weapon of this.weapons) {
        weapon.update(weaponDt, player, target, this.pool);
        // fireFlash resets to exactly this constant on the tick a weapon
        // fires (see Weapon.update) — comparing against it detects "fired
        // this tick" with zero changes to Weapon or its subclasses. Orbit
        // Ward has no discrete fire moment (it overrides update entirely)
        // and so correctly never emits this.
        if (weapon.fireFlash === WEAPON_FIRE_FLASH_DURATION) {
          this.eventBus.emit('weapon:fired', { name: weapon.name });
          // Generic opt-in hook for big centered AoE bursts (Nova Pulse
          // today) — a weapon sets `lastBurstRadius` on the tick it fires
          // and JuiceDirector scales its feedback to that radius instead
          // of using the same tiny reaction every single-target hit gets.
          // `undefined`/falsy for every weapon that doesn't set it.
          if (weapon.lastBurstRadius) {
            this.eventBus.emit('weapon:aoe-burst', {
              x: player.x, y: player.y, radius: weapon.lastBurstRadius, color: weapon.config.color, name: weapon.name,
            });
          }
        }
      }
    }

    this.pool.update(dt);
    this._checkCollisions(player);
    // After the enemy pass: a shot that found a real target this frame has
    // already been spent, so a bullet never steals a hit from the boss.
    this._checkShootableBullets(player);
    this._updateDotEffects(dt, this._getDamageMultiplier(player));
    this._updateIgnite(dt);
    // Hush mutes output, aura included ("your weapons AND aura go dumb"), and
    // a glyph is output too — a field of marks that kept firing through a hush
    // zone would be the one build the Great Hush can't touch.
    if (!player.muted) {
      this._updateAura(dt, player);
      this._updateGlyphs(dt, player);
    }
    this._updateDashChain(dt);
    this._checkDashAttack(player);
    // Last, so everything that could have killed something this frame — shots,
    // AoE, dash, DoT ticks — has already resolved and the corpses are all
    // present to be read (see _resolveRefrain).
    this._resolveRefrain(player);
  }

  _checkCollisions(player) {
    const enemies = this._getEnemies();
    const dmgMultiplier = this._getDamageMultiplier(player);
    this.pool.forEachActive((proj) => {
      if (proj.hitCooldown > 0) return; // still on cooldown from a previous hit (Orbit Ward, etc.)
      for (const enemy of enemies) {
        const dx = proj.x - enemy.x;
        const dy = proj.y - enemy.y;
        const dist = Math.hypot(dx, dy);
        if (dist < proj.radius + enemy.radius) {
          // Desperation is a survival phase, not a damage phase. Consume
          // shots that reach the boss so they cannot keep colliding or seed
          // Ignite/DoT effects while the boss is invulnerable — there's
          // nothing else in the arena during a boss fight for the shot to
          // matter to.
          if (enemy.isBossTier && (enemy.isDying || enemy.invulnerable)) {
            proj.active = false;
            break;
          }
          // A shielded minion (e.g. Warden, invulnerable while its linked
          // minion is alive) blocks damage but shouldn't burn a piercing
          // shot's hit charge on a no-op the way the boss case above does —
          // it's standing in a crowd of other killable minions, so let the
          // shot pass through for free and keep checking the rest of
          // `enemies` for something it can actually hit this tick.
          if (!enemy.isBossTier && enemy.invulnerable) continue;
          // Mismatched tone (Mirror-Shade): pass straight through, for the
          // same reason a shielded Warden does — the shot shouldn't burn a
          // pierce charge on something it was never able to hurt, and there
          // may be a correctly-toned sibling right behind it.
          if (this._toneMismatched(player, enemy)) continue;
          // Executioner only boosts damage against minions, never the boss —
          // same isBossTier idiom already used for knockback below.
          const targetMultiplier = !enemy.isBossTier ? (player.minionDamageMultiplier ?? 1) : (player.bossDamageMultiplier ?? 1);
          const crit = this._rollCrit(player);
          const finalDamage = proj.damage * dmgMultiplier * targetMultiplier
            * this._proximityMultiplier(player, enemy) * this._fullHpTargetMultiplier(player, enemy) * crit;
          enemy.takeDamage(finalDamage);
          this._bankRefrain(player, enemy, finalDamage);
          if (crit > 1) {
            this.eventBus.emit('combat:crit', { x: enemy.x, y: enemy.y, amount: finalDamage });
          }
          this._emitHit(proj, enemy, dx, dy);
          if (this.ignitePercent > 0) this._applyIgnite(enemy, finalDamage * this.ignitePercent);
          // Judgment (Rainbow Reaper pet): finish off a wounded non-boss enemy
          // outright once its remaining HP falls under the threshold fraction.
          if ((player.executeThreshold ?? 0) > 0 && !enemy.isBossTier && !enemy.invulnerable
              && enemy.maxHp && enemy.hp > 0 && enemy.hp <= enemy.maxHp * player.executeThreshold) {
            enemy.takeDamage(enemy.hp);
            this.eventBus.emit('combat:execute', { x: enemy.x, y: enemy.y });
          }
          if (proj.appliesDot) this._applyDotStack(enemy, proj.dotDamagePerTick);
          // Orbit Ward's shards shove struck minions away — never the boss,
          // whose position is driven by its own attack patterns and would
          // desync if nudged (same reasoning as the dash-attack knockback skip).
          if (proj.knocksBack && !enemy.isBossTier) {
            const len = dist || 1;
            enemy.x += (-dx / len) * proj.knockbackForce;
            enemy.y += (-dy / len) * proj.knockbackForce;
          }
          proj.hitsRemaining--;
          if (proj.hitsRemaining <= 0) {
            proj.active = false;
          } else {
            proj.hitCooldown = proj.hitCooldownDuration;
          }
          break; // a projectile only lands on one enemy per tick
        }
      }
    });
  }

  /**
   * Aria's Refrain (data/characters.js): stores a share of the damage she
   * deals inside the target, to be released when it dies (_resolveRefrain).
   *
   * Banked on the enemy instance as an ad-hoc field for the same reason the
   * DoT/ignite state below is — the enemy classes share no base class to add
   * a real field to, and it's collected along with the corpse.
   *
   * Boss-tier enemies are skipped outright: they never release an echo, so
   * banking into them would only be bookkeeping nobody reads.
   */
  _bankRefrain(player, enemy, damage) {
    const cfg = player.refrainConfig;
    if (!cfg || enemy.isBossTier) return;
    enemy._refrainEcho = (enemy._refrainEcho ?? 0) + damage * cfg.echoFraction;
  }

  /**
   * Releases the echo banked in anything that died this frame, and lets a kill
   * made *by* that release carry it onward — the cascade that is Aria's whole
   * identity.
   *
   * Timing is what makes this a single tidy pass: MinionWaveSystem culls dead
   * minions at the top of the next frame (it runs before combat.update), so
   * between a kill and that cull there is exactly one window where a corpse is
   * still in `this.minions` and can be asked what it was carrying.
   *
   * The cascade is breadth-first and bounded twice over — `maxChain` caps the
   * depth, and each generation's power is cut by `chainFalloff`. Carriers left
   * holding an echo when the depth cap is reached are spent without firing, or
   * they'd be picked up again next frame and chain forever.
   */
  _resolveRefrain(player) {
    const cfg = player.refrainConfig;
    if (!cfg) return;
    let carriers = this.minions.filter((m) => m.isDead && m._refrainEcho > 0 && !m._refrainSpent);
    for (let depth = 0; depth < cfg.maxChain && carriers.length > 0; depth++) {
      const next = [];
      for (const carrier of carriers) {
        carrier._refrainSpent = true;
        const power = carrier._refrainEcho;
        carrier._refrainEcho = 0;
        this.eventBus.emit('refrain:carry', { x: carrier.x, y: carrier.y, radius: cfg.radius });
        for (const enemy of this._getEnemies()) {
          if (enemy === carrier || enemy.isDead) continue;
          if ((enemy.isBossTier && enemy.isDying) || enemy.invulnerable) continue;
          if (this._toneMismatched(player, enemy)) continue;
          const dist = Math.hypot(enemy.x - carrier.x, enemy.y - carrier.y);
          if (dist >= cfg.radius + enemy.radius) continue;
          enemy.takeDamage(power);
          // Killed by the echo — it carries, weakened. Not routed through
          // _bankRefrain: the next generation's power is the falloff of this
          // one, not a share of the damage dealt.
          if (enemy.isDead && !enemy.isBossTier && !enemy._refrainSpent) {
            enemy._refrainEcho = power * cfg.chainFalloff;
            next.push(enemy);
          }
        }
      }
      carriers = next;
    }
    for (const stranded of carriers) {
      stranded._refrainSpent = true;
      stranded._refrainEcho = 0;
    }
  }

  /**
   * Iris's Killing Edge (data/characters.js): returns the damage multiplier
   * for this hit — her crit multiplier on a successful roll, otherwise 1.
   * Zero Hour skips the roll and crits outright. Every champion but Iris has
   * critChance 0, so this is a single comparison for them.
   */
  _rollCrit(player) {
    const chance = player.critChance ?? 0;
    if (chance <= 0 && !this.guaranteedCrit) return 1;
    if (this.guaranteedCrit || Math.random() < chance) return player.critMultiplier ?? 1;
    return 1;
  }

  /**
   * Close-range damage bonuses, resolved per hit. Two champions want one and
   * they stack: Thorn's Briar Aura (the reward for fighting at arm's length)
   * and Fugue's Answer stance, whose whole trade is mitigation plus a bite at
   * contact range in exchange for Statement's fire rate.
   */
  _proximityMultiplier(player, enemy) {
    const dist = Math.hypot(enemy.x - player.x, enemy.y - player.y);
    let multiplier = 1;
    if (this.aura?.closeRangeBonus && dist < this.aura.radius + enemy.radius) {
      multiplier *= 1 + this.aura.closeRangeBonus;
    }
    const stances = player.stanceConfig;
    if (stances && player.inStance('answer') && dist < stances.answerRange + enemy.radius) {
      multiplier *= 1 + stances.answerCloseBonus;
    }
    return multiplier;
  }

  // Impact point + normalized travel direction for JuiceDirector's
  // directional hit sparks. Additive alongside the entity's own
  // boss:damaged/minion:damaged emissions, whose payloads deliberately
  // stay untouched — they're a public contract (see Boss.test.js). Orbit
  // shards can be momentarily stationary (prevX/prevY equal), so fall
  // back to the projectile->enemy radial as the impact direction.
  _emitHit(proj, enemy, dx, dy) {
    let hdx = proj.x - proj.prevX;
    let hdy = proj.y - proj.prevY;
    if (hdx === 0 && hdy === 0) {
      hdx = -dx;
      hdy = -dy;
    }
    const len = Math.hypot(hdx, hdy) || 1;
    this.eventBus.emit('combat:hit', {
      x: proj.x, y: proj.y, dirX: hdx / len, dirY: hdy / len, isBossTier: !!enemy.isBossTier,
    });
  }

  // Ad-hoc fields tacked onto the enemy instance rather than a real class
  // field — Boss/Skirmisher/Bomber/Charger share no base class to add one
  // to, and these are garbage-collected along with the enemy when it dies,
  // so no cleanup bookkeeping is needed.
  _applyDotStack(enemy, damagePerStack) {
    if (enemy.invulnerable) return;
    enemy._owDotStacks = Math.min(DOT_MAX_STACKS, (enemy._owDotStacks || 0) + 1);
    enemy._owDotDuration = DOT_STACK_DURATION;
    enemy._owDotDamagePerStack = damagePerStack;
    enemy._owDotTickTimer ??= DOT_TICK_INTERVAL;
  }

  // Same ad-hoc-fields-on-the-enemy idiom as the Orbit Ward DoT below —
  // no shared enemy base class, and the fields die with the enemy. Burn
  // ticks go through takeDamage like every other damage source, so they
  // feed XP/lifesteal/ultimate charge — but only direct projectile hits
  // apply new Ignite (a burn tick can never re-ignite), so no feedback loop.
  _applyIgnite(enemy, amount) {
    if (enemy.invulnerable) return;
    enemy._igniteRemaining = (enemy._igniteRemaining || 0) + amount;
    enemy._igniteTimeLeft = IGNITE_DURATION;
    enemy._igniteTickTimer ??= IGNITE_TICK_INTERVAL;
  }

  _updateIgnite(dt) {
    for (const enemy of this._getEnemies()) {
      if (!enemy._igniteRemaining) continue;
      enemy._igniteTimeLeft -= dt;
      // Window expired between ticks — flush whatever's left as one final
      // tick so the pool always pays out in full.
      if (enemy._igniteTimeLeft <= 0) {
        enemy.takeDamage(enemy._igniteRemaining);
        enemy._igniteRemaining = 0;
        continue;
      }
      enemy._igniteTickTimer -= dt;
      if (enemy._igniteTickTimer <= 0) {
        enemy._igniteTickTimer = IGNITE_TICK_INTERVAL;
        // Split the pool evenly across the ticks that still fit in the
        // window, so it drains to ~zero right as the window closes.
        const ticksLeft = Math.max(1, Math.ceil(enemy._igniteTimeLeft / IGNITE_TICK_INTERVAL));
        const tick = enemy._igniteRemaining / ticksLeft;
        enemy.takeDamage(tick);
        enemy._igniteRemaining -= tick;
      }
    }
  }

  _updateDotEffects(dt, dmgMultiplier) {
    for (const enemy of this._getEnemies()) {
      if (!enemy._owDotStacks) continue;
      enemy._owDotDuration -= dt;
      if (enemy._owDotDuration <= 0) {
        enemy._owDotStacks = 0;
        continue;
      }
      enemy._owDotTickTimer -= dt;
      if (enemy._owDotTickTimer <= 0) {
        enemy._owDotTickTimer = DOT_TICK_INTERVAL;
        enemy.takeDamage(enemy._owDotDamagePerStack * enemy._owDotStacks * dmgMultiplier);
      }
    }
  }

  // Thorn's Briar Aura: a standing ring of damage centered on the player,
  // paid out on AURA_TICK_INTERVAL beats. No-op for every other champion.
  _updateAura(dt, player) {
    if (this.aura) {
      this._auraTimer -= dt;
      if (this._auraTimer <= 0) {
        this._auraTimer += AURA_TICK_INTERVAL;
        this.applyAreaDamage({
          x: player.x,
          y: player.y,
          radius: this.aura.radius,
          damage: this.aura.damagePerSecond * AURA_TICK_INTERVAL,
          player,
        });
      }
    }
    // Searing Corona (Rainbow Corona pet): a second, pet-owned aura on its own
    // timer, so it lands whether or not the champion also has a Briar Aura.
    if (this.petAura) {
      this._petAuraTimer -= dt;
      if (this._petAuraTimer <= 0) {
        this._petAuraTimer += AURA_TICK_INTERVAL;
        this.applyAreaDamage({
          x: player.x,
          y: player.y,
          radius: this.petAura.radius,
          damage: this.petAura.damagePerSecond * AURA_TICK_INTERVAL,
          player,
        });
      }
    }
  }

  /**
   * Quill's Inscription (Act III): each glyph the player has written pulses
   * damage where it stands. The glyphs themselves live on Player (they're
   * player state, and the renderer and HUD read them); the damage resolves here
   * because everything that deals damage in this game resolves here, and so
   * picks up the run's build multipliers for free.
   *
   * Fair Copy (Quill's ultimate) sets `inscriptionInstant`, under which the
   * lattice also chains: each glyph additionally strikes toward the next, which
   * is what turns a field of marks into a single connected shape.
   */
  _updateGlyphs(dt, player) {
    const cfg = player.inscriptionConfig;
    if (!cfg || !player.glyphs?.length) return;
    this._glyphTimer = (this._glyphTimer ?? cfg.pulseInterval) - dt;
    if (this._glyphTimer > 0) return;
    this._glyphTimer += cfg.pulseInterval;
    const glyphs = player.glyphs;
    for (let i = 0; i < glyphs.length; i++) {
      const glyph = glyphs[i];
      this.applyAreaDamage({
        x: glyph.x, y: glyph.y, radius: glyph.radius, damage: cfg.pulseDamage, player,
      });
      if (!player.inscriptionInstant || glyphs.length < 2) continue;
      // The tether to the next glyph, sampled as a few points along the line —
      // the same "a beam is overlapping stationary hits" trick the Umbral
      // Lantern uses, rather than a new damage primitive for one ultimate.
      const next = glyphs[(i + 1) % glyphs.length];
      for (let s = 1; s < 4; s++) {
        const t = s / 4;
        this.applyAreaDamage({
          x: glyph.x + (next.x - glyph.x) * t,
          y: glyph.y + (next.y - glyph.y) * t,
          radius: cfg.chainRadius,
          damage: cfg.chainDamage,
          player,
        });
      }
    }
    this.eventBus.emit('glyphs:pulsed', { count: glyphs.length, chained: player.inscriptionInstant });
  }

  // Kestrel's Slipstream: the chain window and the refund's own cooldown both
  // count down here. Letting the window lapse drops the whole chain, not one
  // stack — it's a "keep moving" streak, same shape as Vess's Cadence.
  _updateDashChain(dt) {
    if (this._dashRefundTimer > 0) this._dashRefundTimer -= dt;
    if (!this.dashChain || this._dashChainStacks === 0) return;
    this._dashChainTimer -= dt;
    if (this._dashChainTimer <= 0) {
      this._dashChainStacks = 0;
      this.eventBus.emit('dash:chain-lost');
    }
  }

  /**
   * Damages every enemy inside a world-space circle, scaled by the run's
   * damage multipliers exactly as a weapon hit would be. The public entry
   * point for damage that isn't a projectile — champion ultimates
   * (data/ultimates.js) and the Briar Aura above both land through here.
   * Returns how many enemies were hit.
   */
  applyAreaDamage({ x, y, radius, damage, player, knockback = 0 }) {
    const dmgMultiplier = this._getDamageMultiplier(player);
    let hits = 0;
    for (const enemy of this._getEnemies()) {
      // Same guards as _applyDashAoe: a dying/invulnerable target can't be
      // damaged, but nothing else about the burst changes because of it.
      if ((enemy.isBossTier && enemy.isDying) || enemy.invulnerable) continue;
      if (this._toneMismatched(player, enemy)) continue;
      const dx = enemy.x - x;
      const dy = enemy.y - y;
      const dist = Math.hypot(dx, dy);
      if (dist >= radius + enemy.radius) continue;
      hits++;
      const targetMultiplier = !enemy.isBossTier ? (player.minionDamageMultiplier ?? 1) : (player.bossDamageMultiplier ?? 1);
      const finalDamage = damage * dmgMultiplier * targetMultiplier;
      enemy.takeDamage(finalDamage);
      this._bankRefrain(player, enemy, finalDamage);
      if (this.ignitePercent > 0) this._applyIgnite(enemy, finalDamage * this.ignitePercent);
      // Never shove a boss-tier enemy — its position is driven by its own
      // attack patterns and nudging it desyncs them (see _applyDashAoe).
      if (knockback > 0 && !enemy.isBossTier) {
        const len = dist || 1;
        enemy.x += (dx / len) * knockback;
        enemy.y += (dy / len) * knockback;
      }
    }
    return hits;
  }

  /** Drags non-boss enemies inside the circle `distance` px toward its center (Thornsong). */
  pullEnemies({ x, y, radius, distance }) {
    for (const enemy of this.minions) {
      if (enemy.isBossTier) continue;
      const dx = x - enemy.x;
      const dy = y - enemy.y;
      const dist = Math.hypot(dx, dy);
      if (dist > radius || dist < 1) continue;
      // Never overshoot past the center — a hard pull would otherwise fling
      // close-in minions out the far side and read as a shove, not a vacuum.
      const step = Math.min(distance, dist);
      enemy.x += (dx / dist) * step;
      enemy.y += (dy / dist) * step;
    }
  }

  // While the character has a dashAttack and the player is mid-dash, deal
  // bonus damage + a small AoE burst on first contact with an enemy — gated
  // by `_dashHitLanded` (reset once the dash ends) so a single dash can
  // only trigger this once, the same "no multi-hit per short window" shape
  // as the pooled-projectile hitCooldown used by _checkCollisions above.
  _checkDashAttack(player) {
    if (!this.dashAttack) return;
    if (!player.isDashing) {
      this._dashHitLanded = false;
      return;
    }
      if (this._dashHitLanded) return;

    for (const enemy of this._getEnemies()) {
      const dist = Math.hypot(enemy.x - player.x, enemy.y - player.y);
      if (dist < enemy.radius + player.radius) {
        this._dashHitLanded = true;
        this._applyDashAoe(enemy, player);
        return;
      }
    }
  }

  _applyDashAoe(contactEnemy, player) {
    // Base strike, scaled by the Slipstream chain built so far and by
    // Skybreaker if it's running (both 1 when neither applies).
    const damage = this.dashAttack.damage * this.dashChainMultiplier * this.dashDamageMultiplier;
    const radius = this.dashAttack.radius * this.dashRadiusMultiplier;
    const dmgMultiplier = this._getDamageMultiplier(player);
    for (const enemy of this._getEnemies()) {
      // isDying is boss-only (see Boss.js); invulnerable applies to any
      // enemy (boss desperation or a shielded minion like Warden) — the AoE
      // still triggers off contact with a shielded target, it just can't
      // damage that target specifically, same reasoning as the pass-through
      // in _checkCollisions above.
      if ((enemy.isBossTier && enemy.isDying) || enemy.invulnerable) continue;
      if (this._toneMismatched(player, enemy)) continue;
      const dist = Math.hypot(enemy.x - contactEnemy.x, enemy.y - contactEnemy.y);
      if (dist < radius) {
        const targetMultiplier = !enemy.isBossTier ? (player.minionDamageMultiplier ?? 1) : (player.bossDamageMultiplier ?? 1);
        const finalDamage = damage * dmgMultiplier * targetMultiplier * this._rollCrit(player);
        enemy.takeDamage(finalDamage);
        this._bankRefrain(player, enemy, finalDamage);
        if (this.ignitePercent > 0) this._applyIgnite(enemy, finalDamage * this.ignitePercent);
        // Shove struck minions away from the player for extra impact (a direct
        // one-shot position nudge — they path back on their own). Skip any
        // boss-tier enemy: it drives its own position (boss.locked / attack
        // patterns) and nudging it would desync those.
        if (!enemy.isBossTier) {
          const dx = enemy.x - player.x;
          const dy = enemy.y - player.y;
          const len = Math.hypot(dx, dy) || 1;
          enemy.x += (dx / len) * DASH_ENEMY_KNOCKBACK;
          enemy.y += (dy / len) * DASH_ENEMY_KNOCKBACK;
        }
      }
    }
    // Kestrel's Slipstream: connecting extends the chain and hands the dash
    // charge straight back, so a good read turns into another dash instead
    // of a wait. The refund rides its own short cooldown — without it, a
    // dense wave would keep a dash permanently available for free.
    if (this.dashChain) {
      this._dashChainStacks = Math.min(this.dashChain.maxStacks, this._dashChainStacks + 1);
      this._dashChainTimer = this.dashChain.window;
      if (this._dashRefundTimer <= 0) {
        this._dashRefundTimer = this.dashChain.refundCooldown;
        player.refundDashCharge?.();
      }
      this.eventBus.emit('dash:chain', {
        stacks: this._dashChainStacks, x: contactEnemy.x, y: contactEnemy.y,
      });
    }

    // Bounce the player back off the enemy they slammed into — the "recoil"
    // that makes a landed dash feel like it connected. Juice (shake/hit-stop/
    // particles) is wired off dash:hit in Game.js._wireJuice.
    player.applyKnockback(player.x - contactEnemy.x, player.y - contactEnemy.y, DASH_PLAYER_BOUNCE);
    this.eventBus.emit('dash:hit', { x: contactEnemy.x, y: contactEnemy.y });
  }

  forEachProjectile(fn) {
    this.pool.forEachActive(fn);
  }

  /** Projectiles flagged to destroy boss bullets (Orbit Ward shards) — see Game.js's cross-pool block check. */
  forEachBlockingProjectile(fn) {
    this.pool.forEachActive((proj) => {
      if (proj.blocksBullets) fn(proj);
    });
  }
}
